import type { StreakHistoryEntry } from '@/types/statistics'

interface StreakCalendarProps {
  history: StreakHistoryEntry[]
  currentStreak: number
  longestStreak: number
}

function activityColor(completed: boolean, idx: number, history: StreakHistoryEntry[]): string {
  if (!completed) return 'bg-muted'
  // Color based on recent-ness for visual interest (last 30 days brighter)
  const daysAgo = history.length - 1 - idx
  if (daysAgo <= 7) return 'bg-green-600'
  if (daysAgo <= 30) return 'bg-green-500'
  return 'bg-green-400'
}

export function StreakCalendar({ history, currentStreak, longestStreak }: StreakCalendarProps) {
  // Build a grid: weeks as columns, days of week as rows (Mon–Sun)
  // history is sorted oldest→newest, length = 90
  const weeks: StreakHistoryEntry[][] = []
  const startDate = new Date(history[0]?.date ?? new Date())
  // Pad so first column starts on Monday
  const dayOfWeek = (startDate.getDay() + 6) % 7 // 0=Mon, 6=Sun
  const paddedHistory = [...Array(dayOfWeek).fill(null), ...history]

  for (let i = 0; i < paddedHistory.length; i += 7) {
    weeks.push(paddedHistory.slice(i, i + 7) as StreakHistoryEntry[])
  }

  const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold leading-none tabular-nums">{currentStreak}</span>
          <span className="text-sm text-muted-foreground">day streak</span>
        </div>
        <div className="text-sm text-muted-foreground">
          Longest: <span className="font-semibold text-foreground">{longestStreak} days</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-flex gap-1">
          {/* Day labels */}
          <div className="flex flex-col gap-1 pr-1">
            {DAY_LABELS.map((d) => (
              <div key={d} className="flex h-4 w-7 items-center text-xs text-muted-foreground">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          {weeks.map((week, wi) => (
            <div key={wi} className="flex flex-col gap-1">
              {Array.from({ length: 7 }).map((_, di) => {
                const entry = week[di]
                if (!entry) {
                  return <div key={di} className="h-4 w-4 rounded-sm" />
                }
                const histIdx = history.findIndex((h) => h.date === entry.date)
                return (
                  <div
                    key={di}
                    title={`${entry.date}${entry.completed ? ' ✓' : ''}`}
                    className={`h-4 w-4 cursor-default rounded-sm transition-colors ${activityColor(entry.completed, histIdx, history)}`}
                  />
                )
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Less</span>
        <div className="h-3 w-3 rounded-sm bg-muted" />
        <div className="h-3 w-3 rounded-sm bg-green-400" />
        <div className="h-3 w-3 rounded-sm bg-green-500" />
        <div className="h-3 w-3 rounded-sm bg-green-600" />
        <span>More</span>
      </div>
    </div>
  )
}
